import { DOCUMENT } from '@angular/common';
import { DestroyRef, effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { merge, fromEvent, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { AuthService } from '../../../features/authentication/data-access/auth.service';
import { TokenStorageService } from './token-storage.service';

/** How long the user may sit idle before the "Session Expiring Soon" dialog appears. */
const IDLE_WARNING_AFTER_MS = 29 * 60 * 1000;

/** How long the "you are about to be signed out" dialog stays up before logout. */
const IDLE_WARNING_MS = 60 * 1000;

/** Idle time until sign-out: 30 minutes (the dialog after 29 minutes, then its one-minute countdown). */
const IDLE_TIMEOUT_MS = IDLE_WARNING_AFTER_MS + IDLE_WARNING_MS;

/**
 * Refresh once this much of the access token's lifetime has elapsed, so the new
 * token is in hand well before the old one dies.
 */
const REFRESH_AT_LIFETIME_FRACTION = 0.75;

/** Never schedule a refresh closer than this to now, to avoid a tight retry loop. */
const MIN_REFRESH_DELAY_MS = 5 * 1000;

/** setTimeout takes a signed 32-bit delay; anything larger fires immediately. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly showWarning = signal(false);
  readonly remainingSeconds = signal(0);
  /**
   * True when the warning is up because the sign-in session itself (the refresh token) is about to end, not
   * because of inactivity: extending cannot help then, only signing in again.
   */
  readonly sessionEnding = signal(false);
  /** Whole minutes of inactivity before the warning appears. */
  readonly idleMinutes = Math.round(IDLE_WARNING_AFTER_MS / 60_000);
  /** Length of the warning's countdown, in seconds. */
  readonly warningSeconds = IDLE_WARNING_MS / 1000;

  /** The page title before the countdown took it over; null while it is not shown. */
  private savedTitle: string | null = null;

  private activitySubscription: Subscription | null = null;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private logoutDeadline = 0;
  private trackingEnabled = false;
  private refreshInFlight = false;

  constructor() {
    effect(() => {
      if (this.authService.currentUserSignal()) {
        this.start();
        return;
      }

      this.stop();
    });
  }

  start(): void {
    if (this.trackingEnabled) {
      this.resetTimers();
      return;
    }

    this.trackingEnabled = true;
    this.bindActivityTracking();
    this.resetTimers();
  }

  stop(): void {
    this.trackingEnabled = false;
    this.refreshInFlight = false;
    this.showWarning.set(false);
    this.sessionEnding.set(false);
    this.remainingSeconds.set(0);
    this.clearTimers();
    this.activitySubscription?.unsubscribe();
    this.activitySubscription = null;
  }

  /** "Extend session" in the warning dialog: the idle clock starts over. */
  extendSession(): void {
    if (!this.trackingEnabled) {
      return;
    }

    this.resetTimers();
  }

  /** "Sign out" in the warning dialog: the user's own choice, so the login page shows no notice. */
  logoutNow(): void {
    this.handleTimeoutLogout(null);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private bindActivityTracking(): void {
    if (this.activitySubscription) {
      return;
    }

    // Include touch events for mobile devices
    this.activitySubscription = merge(
      fromEvent(this.document, 'mousemove'),
      fromEvent(this.document, 'keydown'),
      fromEvent(this.document, 'click'),
      fromEvent(this.document, 'touchstart'),
      fromEvent(this.document, 'touchmove'),
      fromEvent(window, 'scroll')
    )
      .pipe(throttleTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.trackingEnabled || !this.authService.currentUserSignal()) {
          return;
        }

        // While the warning dialog is up the user must answer it explicitly;
        // stray mouse movement shouldn't silently dismiss it.
        if (this.showWarning()) {
          return;
        }

        this.resetTimers();
      });
  }

  /**
   * Re-arms every timer. The idle countdown restarts from now; the token refresh
   * is scheduled from the current access token's expiry. Activity extends the
   * session, but never past the refresh token's own expiry.
   */
  private resetTimers(): void {
    if (!this.trackingEnabled) {
      return;
    }

    this.clearTimers();
    this.showWarning.set(false);
    this.remainingSeconds.set(0);

    this.scheduleTokenRefresh();

    // The hard ceiling: once the refresh token dies the session cannot continue,
    // no matter how active the user is.
    const sessionEndsIn = this.millisecondsUntil(this.tokenStorage.getRefreshTokenExpiresOn());

    if (sessionEndsIn !== null && sessionEndsIn <= 0) {
      this.handleTimeoutLogout('session');
      return;
    }

    const sessionEndsFirst = sessionEndsIn !== null && sessionEndsIn < IDLE_TIMEOUT_MS;
    const logoutDelay = sessionEndsFirst ? sessionEndsIn : IDLE_TIMEOUT_MS;
    const warningDelay = Math.max(logoutDelay - IDLE_WARNING_MS, 0);
    const reason: SignOutReason = sessionEndsFirst ? 'session' : 'idle';

    this.warningTimer = this.setTimer(() => {
      this.sessionEnding.set(sessionEndsFirst);
      this.showWarning.set(true);
      this.logoutDeadline = Date.now() + Math.max(logoutDelay - warningDelay, 0);
      this.updateRemainingSeconds();
      this.countdownTimer = setInterval(() => {
        this.updateRemainingSeconds();
      }, 1000);
    }, warningDelay);

    this.logoutTimer = this.setTimer(() => {
      this.handleTimeoutLogout(reason);
    }, logoutDelay);
  }

  /**
   * Renew the access token before it expires. This is what keeps an active user
   * signed in: without it the session would end the moment the access token
   * lapsed, even with a perfectly valid refresh token still in storage.
   */
  private scheduleTokenRefresh(): void {
    const expiresIn = this.millisecondsUntil(this.tokenStorage.getAccessTokenExpiresOn());

    if (expiresIn === null) {
      return;
    }

    // Refresh partway through the token's life so a failure still leaves time to retry.
    const delay = Math.max(expiresIn * REFRESH_AT_LIFETIME_FRACTION, MIN_REFRESH_DELAY_MS);

    this.refreshTimer = this.setTimer(() => this.refreshToken(), delay);
  }

  private refreshToken(): void {
    if (!this.trackingEnabled || this.refreshInFlight) {
      return;
    }

    if (this.tokenStorage.isRefreshTokenExpired()) {
      this.handleTimeoutLogout('session');
      return;
    }

    this.refreshInFlight = true;

    this.authService.refreshAccessToken().subscribe({
      next: () => {
        this.refreshInFlight = false;

        if (!this.trackingEnabled) {
          return;
        }

        // Re-arm the refresh against the new expiry. The idle countdown is left
        // alone: a background refresh is not user activity.
        this.clearTimer('refresh');
        this.scheduleTokenRefresh();
      },
      error: () => {
        this.refreshInFlight = false;

        if (!this.trackingEnabled) {
          return;
        }

        // A failed refresh may just be a blip. Only end the session if the
        // refresh token is genuinely spent; otherwise back off and retry.
        if (this.tokenStorage.isRefreshTokenExpired()) {
          this.handleTimeoutLogout('session');
          return;
        }

        this.clearTimer('refresh');
        this.refreshTimer = this.setTimer(() => this.refreshToken(), MIN_REFRESH_DELAY_MS);
      },
    });
  }

  /**
   * Milliseconds from now until an ISO timestamp, or null when absent/unparseable.
   * A timestamp carrying no timezone is read as UTC, matching the backend, rather
   * than as the viewer's local time.
   */
  private millisecondsUntil(expiresOn: string | null): number | null {
    if (!expiresOn) {
      return null;
    }

    const normalized = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(expiresOn.trim())
      ? expiresOn.trim()
      : `${expiresOn.trim()}Z`;

    const expiryMs = Date.parse(normalized);

    if (Number.isNaN(expiryMs)) {
      return null;
    }

    return expiryMs - Date.now();
  }

  private setTimer(callback: () => void, delay: number): ReturnType<typeof setTimeout> {
    const safeDelay = Math.min(Math.max(delay, 0), MAX_TIMEOUT_MS);
    return setTimeout(callback, safeDelay);
  }

  private updateRemainingSeconds(): void {
    const remainingMs = Math.max(this.logoutDeadline - Date.now(), 0);
    const seconds = Math.ceil(remainingMs / 1000);
    this.remainingSeconds.set(seconds);
    // The dialog is invisible from another tab or window; the tab title still shows the countdown there.
    this.savedTitle ??= this.document.title;
    this.document.title = `(${formatCountdown(seconds)}) Session expiring`;
  }

  private restoreTitle(): void {
    if (this.savedTitle !== null) {
      this.document.title = this.savedTitle;
      this.savedTitle = null;
    }
  }

  /** reason tells the login page why the user is back there (?reason=idle|session); null shows nothing. */
  private handleTimeoutLogout(reason: SignOutReason | null): void {
    const returnUrl = this.router.url.startsWith('/auth') ? '/' : this.router.url;
    this.stop();
    // Revoke the refresh token on the server too, not only the local copy.
    this.authService.signOut().subscribe(() => {
      void this.router.navigate(['/auth/login'], {
        queryParams: {
          ...(reason ? { reason } : {}),
          ...(returnUrl && returnUrl !== '/' ? { returnUrl } : {}),
        },
      });
    });
  }

  private clearTimer(which: 'refresh'): void {
    if (which === 'refresh' && this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private clearTimers(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }

    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }

    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    this.restoreTitle();
  }
}

/** Why the user was signed out automatically: inactivity, or the sign-in session reaching its end. */
export type SignOutReason = 'idle' | 'session';

/** 75 -> "1:15", 9 -> "0:09". */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
}
